/**
 * Help Center marketing page (`/help-center`) — GitHub issue "Create
 * React components for static marketing pages using vite-react-ssg".
 * Statically generated; no client-side data fetching or auth dependency.
 *
 * FAQ entries use native `<details>`/`<summary>` — natively keyboard
 * operable and exposed to assistive tech with no extra ARIA plumbing
 * (WCAG 2.1 AA, Requirement #4), and fully present in server-rendered
 * HTML (crawlable without executing JS).
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import MarketingLayout from '@/components/marketing/MarketingLayout';
import SeoHead from '@/components/marketing/SeoHead';
import { Button } from '@/components/ui/button';

const FAQ_KEYS = ['q1', 'q2', 'q3'] as const;

export default function HelpCenterPage(): JSX.Element {
  const { t } = useTranslation();

  return (
    <MarketingLayout>
      <SeoHead
        title={t('marketing.helpCenter.title')}
        description={t('marketing.helpCenter.description')}
        path="/help-center"
      />

      <section className="container mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
          {t('marketing.helpCenter.heading')}
        </h1>
        <p className="mt-4 text-xl text-muted-foreground">{t('marketing.helpCenter.subheading')}</p>
      </section>

      <section aria-label={t('marketing.helpCenter.heading')} className="border-t bg-muted/30">
        <div className="container mx-auto max-w-3xl px-4 py-16">
          <div className="space-y-4">
            {FAQ_KEYS.map((key) => (
              <details key={key} className="group rounded-lg border bg-card p-4">
                <summary className="cursor-pointer text-lg font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  {t(`marketing.helpCenter.faq.${key}.question`)}
                </summary>
                <p className="mt-2 text-muted-foreground">
                  {t(`marketing.helpCenter.faq.${key}.answer`)}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 text-center">
        <Button size="lg" asChild>
          <Link to="/contact">{t('marketing.helpCenter.cta')}</Link>
        </Button>
      </section>
    </MarketingLayout>
  );
}
